import React, { useEffect, useState } from "react";
import { DateInput } from "@/components/DateInput";
import { Plus, Trash2, Loader2, Save, UserMinus } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Avatar } from "./AvatarStack";

const inp = "w-full h-10 px-3 rounded-lg border border-neutral-200 focus:border-brand focus:outline-none text-sm bg-white disabled:bg-slate-50 disabled:text-slate-400";
const lbl = "font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-1 block";

function toForm(a) {
  return {
    mode: a.allocation_periods?.length ? "periods" : "fixed",
    allocated_hours: a.allocated_hours ?? 0,
    permanent: !!a.permanent, start_date: a.start_date || "", end_date: a.end_date || "",
    periods: (a.allocation_periods || []).map((p) => ({ start_date: p.start_date, end_date: p.end_date, pct: p.pct })),
    contract_hours_month: a.contract_hours_month ?? "", hourly_rate_override: a.hourly_rate_override ?? "",
  };
}

function PeriodRows({ f, set, project }) {
  const upd = (i, patch) => set({ periods: f.periods.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const add = () => {
    const last = f.periods[f.periods.length - 1];
    set({ periods: [...f.periods, { start_date: last ? last.end_date : project?.start_date || "", end_date: last ? "" : project?.end_date || "", pct: last ? last.pct : f.allocated_hours || 100 }] });
  };
  return (
    <div className="space-y-2">
      {f.periods.map((r, i) => (
        <div key={i} className="grid grid-cols-2 gap-2 items-center p-3 rounded-lg bg-slate-50 border border-neutral-100" data-testid={`alloc-period-row-${i}`}>
          <DateInput value={r.start_date || ""} onChange={(e) => upd(i, { start_date: e.target.value })} className={`${inp} min-w-0`} data-testid={`alloc-period-start-${i}`} />
          <DateInput value={r.end_date || ""} onChange={(e) => upd(i, { end_date: e.target.value })} className={`${inp} min-w-0`} data-testid={`alloc-period-end-${i}`} />
          <div className="flex items-center gap-2 min-w-0">
            <input type="number" min="0" max="100" step="5" value={r.pct} onChange={(e) => upd(i, { pct: e.target.value })} className={`${inp} min-w-0 font-mono`} data-testid={`alloc-period-pct-${i}`} />
            <span className="text-sm text-ink-muted">%</span>
          </div>
          <button type="button" onClick={() => set({ periods: f.periods.filter((_, j) => j !== i) })} className="justify-self-end h-9 px-2 rounded-lg text-[12px] text-ink-muted hover:text-red-600 hover:bg-red-50 flex items-center gap-1" data-testid={`alloc-period-remove-${i}`}><Trash2 className="h-4 w-4" /> Șterge</button>
        </div>
      ))}
      <button type="button" onClick={add} data-testid="alloc-period-add" className="flex items-center gap-1 text-[13px] text-brand hover:underline"><Plus className="h-4 w-4" /> Adaugă perioadă</button>
      <p className="text-[11px] text-ink-muted">În afara perioadelor angajatul are 0%. Perioadele se pot înlănțui pe aceeași dată.</p>
    </div>
  );
}

export function AssignmentSheet({ assignment, project, totalLoad, onClose, onSave, onRemove }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (assignment) setF(toForm(assignment)); }, [assignment]);
  const set = (patch) => setF((cur) => ({ ...cur, ...patch }));
  const a = assignment;
  const validPeriods = !f || f.mode === "fixed" || f.periods.every((r) => r.start_date && r.end_date && r.end_date >= r.start_date && r.pct !== "" && +r.pct >= 0 && +r.pct <= 100);

  async function save() {
    setBusy(true);
    const num = (v) => (v === "" || v == null ? null : parseFloat(v));
    const ok = await onSave(a.id, {
      allocated_hours: parseFloat(f.allocated_hours) || 0,
      allocation_periods: f.mode === "periods" ? f.periods.map((r) => ({ ...r, pct: parseFloat(r.pct) || 0 })) : [],
      permanent: f.permanent, start_date: f.permanent ? null : f.start_date || null, end_date: f.permanent ? null : f.end_date || null,
      contract_hours_month: num(f.contract_hours_month), hourly_rate_override: num(f.hourly_rate_override),
    });
    setBusy(false);
    if (ok) onClose();
  }

  return (
    <Sheet open={!!a} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto p-0" data-testid="assignment-sheet">
        {a && f && (
          <div className="flex flex-col min-h-full">
            <SheetHeader className="p-6 border-b border-neutral-100 text-left">
              <div className="flex items-center gap-3">
                <Avatar id={a.employee_id} name={a.employee?.name} size={44} ring={false} />
                <div className="min-w-0">
                  <SheetTitle className="font-display text-lg truncate">{a.employee?.name}</SheetTitle>
                  <SheetDescription className="truncate">{a.employee?.job_title || "—"} · {project?.code}</SheetDescription>
                </div>
              </div>
              <div className={`mt-3 text-[12px] rounded-lg px-3 py-2 ${totalLoad > 100 ? "bg-red-50 text-red-700" : "bg-slate-50 text-ink-muted"}`} data-testid="sheet-total-load">
                Încărcare totală luna aceasta, pe toate proiectele: <b className="tabular-nums">{totalLoad}%</b>{totalLoad > 100 && " — supraalocat"}
              </div>
            </SheetHeader>
            <div className="p-6 space-y-6 flex-1">
              <section>
                <span className={lbl}>Mod alocare</span>
                <div className="grid grid-cols-2 gap-1 p-1 bg-slate-100 rounded-lg">
                  {[["fixed", "Procent fix"], ["periods", "Pe perioade"]].map(([k, t]) => (
                    <button key={k} type="button" data-testid={`sheet-mode-${k}`} onClick={() => set({ mode: k })}
                      className={`h-8 rounded-md text-[13px] font-medium transition-colors ${f.mode === k ? "bg-white shadow-sm text-ink" : "text-ink-muted hover:text-ink"}`}>{t}</button>
                  ))}
                </div>
              </section>
              {f.mode === "fixed" ? (
                <section className="space-y-3">
                  <div>
                    <span className={lbl}>Alocat (%)</span>
                    <input type="number" min="0" max="100" step="5" value={f.allocated_hours} onChange={(e) => set({ allocated_hours: e.target.value })} className={`${inp} font-mono`} data-testid="sheet-pct" />
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer w-fit">
                    <input type="checkbox" checked={f.permanent} onChange={(e) => set({ permanent: e.target.checked })} data-testid="sheet-permanent" /> Alocare permanentă
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <div><span className={lbl}>De la</span><DateInput value={f.start_date} disabled={f.permanent} onChange={(e) => set({ start_date: e.target.value })} className={inp} data-testid="sheet-start" /></div>
                    <div><span className={lbl}>Până la</span><DateInput value={f.end_date} disabled={f.permanent} onChange={(e) => set({ end_date: e.target.value })} className={inp} data-testid="sheet-end" /></div>
                  </div>
                  <p className="text-[11px] text-ink-muted">Fără date: se aplică pe toată durata proiectului.</p>
                </section>
              ) : (
                <section><span className={lbl}>Perioade de alocare</span><PeriodRows f={f} set={set} project={project} /></section>
              )}
              <section className="grid grid-cols-2 gap-3 pt-4 border-t border-neutral-100">
                <div><span className={lbl}>Ore contract / lună</span><input type="number" step="8" placeholder="—" value={f.contract_hours_month} onChange={(e) => set({ contract_hours_month: e.target.value })} className={`${inp} font-mono`} data-testid="sheet-contract" /></div>
                <div>
                  <span className={lbl}>Tarif override (€/h)</span>
                  <input type="number" step="5" placeholder={a.employee?.hourly_fee_rate != null ? `${a.employee.hourly_fee_rate} (din fișă)` : "—"} value={f.hourly_rate_override}
                    onChange={(e) => set({ hourly_rate_override: e.target.value })} className={`${inp} font-mono`} data-testid="sheet-override" />
                </div>
              </section>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-neutral-100 p-4 flex items-center gap-2">
              <button type="button" onClick={() => onRemove(a.id)} data-testid={`unassign-${a.id}`}
                className="h-10 px-3 rounded-lg text-sm text-red-600 hover:bg-red-50 flex items-center gap-1.5"><UserMinus className="h-4 w-4" /> Dezalocă</button>
              <button type="button" onClick={onClose} className="ml-auto h-10 px-4 rounded-lg text-sm text-ink-muted hover:text-ink" data-testid="sheet-cancel">Anulează</button>
              <button type="button" onClick={save} disabled={busy || !validPeriods} data-testid="sheet-save"
                className="h-10 px-4 rounded-lg bg-brand text-white text-sm font-medium flex items-center gap-2 hover:bg-[#2f5290] disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvează
              </button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
